import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { useT } from "../i18n";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import {
  TrendingUp,
  Download,
  Filter,
  Eye,
  Activity,
  Globe,
  RefreshCw,
  BarChart3,
  MousePointerClick,
  Users,
  Flame,
  GitBranch,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import XpnsrPageHeader from "./XpnsrPageHeader";
import { XpnsrCardSkeleton, XpnsrTableSkeleton, XpnsrSkeleton } from "./XpnsrSkeleton";

const COLORS = [
  "#00e5a0",
  "#7af0c4",
  "#b3ffe3",
  "#4dd9aa",
  "#26b888",
  "#1a8d68",
  "#0e6b50",
];

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// ROI is undefined without spend — "0.0%" would wrongly read as break-even.
// Show an em dash when cost is 0.
const fmtRoi = (roi, cost) => (num(cost) > 0 ? `${num(roi).toFixed(1)}%` : '—');

// Wave 18b — render numeric amounts with the workspace currency
// resolved from the ROI / campaigns-summary response. Falls back to
// the legacy "$" prefix when the backend hasn't returned a currency
// (older clients hitting newer servers, or the response just hasn't
// loaded yet).
const formatMoney = (value, currency) => {
  const code = (currency || "").toUpperCase();
  const amount = num(value).toFixed(2);
  if (!code) return `$${amount}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(num(value));
  } catch (_e) {
    return `${amount} ${code}`;
  }
};

const VALID_DAYS = new Set(["1", "7", "14", "30", "90"]);
const VALID_TABS = new Set([
  "overview",
  "campaigns",
  "roi",
  "geo",
  "devices",
  "heatmap",
  "funnel",
  "cohort",
]);

const HEATMAP_CELL_COLORS = [
  "bg-muted/30",
  "bg-emerald-900/40",
  "bg-emerald-800/50",
  "bg-emerald-700/60",
  "bg-emerald-600/70",
  "bg-emerald-500/80",
  "bg-emerald-400",
];

const heatmapColor = (value, max) => {
  if (!max || !value) return HEATMAP_CELL_COLORS[0];
  const ratio = value / max;
  const idx = Math.min(Math.floor(ratio * (HEATMAP_CELL_COLORS.length - 1)) + 1, HEATMAP_CELL_COLORS.length - 1);
  return HEATMAP_CELL_COLORS[idx];
};

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}`);

export default function Analytics() {
  const { user } = useAuth();
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);

  const initialTab = (() => {
    const v = searchParams.get("tab");
    return v && VALID_TABS.has(v) ? v : "overview";
  })();
  const initialDays = (() => {
    const v = searchParams.get("period");
    return v && VALID_DAYS.has(v) ? v : "7";
  })();
  const initialCampaign = searchParams.get("campaign") || "all";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [days, setDays] = useState(initialDays);
  // "Custom range" = an inclusive from/to period (the end defaults to today).
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const todayIso = (() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();
  const isCustom = days === 'custom';
  const effectiveDays = (() => {
    if (!isCustom) return days;
    if (!customFrom) return '7';
    const n = Math.round((new Date(customTo || todayIso) - new Date(customFrom)) / 86400000) + 1;
    return String(Math.min(365, Math.max(1, n)));
  })();
  const [selectedCampaign, setSelectedCampaign] = useState(initialCampaign);

  // Sync filters → URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (days === "7") next.delete("period"); else next.set("period", days);
    if (!selectedCampaign || selectedCampaign === "all") next.delete("campaign"); else next.set("campaign", selectedCampaign);
    if (!activeTab || activeTab === "overview") next.delete("tab"); else next.set("tab", activeTab);
    const nextStr = next.toString();
    if (nextStr !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [days, selectedCampaign, activeTab]);

  const [performanceData, setPerformanceData] = useState([]);
  const [geoData, setGeoData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [campaignsSummary, setCampaignsSummary] = useState([]);
  const [roiData, setRoiData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapMetric, setHeatmapMetric] = useState("clicks");
  const [funnelData, setFunnelData] = useState(null);
  const [cohortData, setCohortData] = useState(null);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchLiveStats, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    Promise.all([
      fetchPerformance(),
      fetchGeo(),
      fetchDevices(),
      fetchCampaignsSummary(),
      fetchRoi(),
      fetchHeatmap(),
      fetchFunnel(),
      fetchCohort(),
    ]);
  }, [effectiveDays, customFrom, customTo, selectedCampaign]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchLiveStats(),
      fetchCampaigns(),
      fetchPerformance(),
      fetchGeo(),
      fetchDevices(),
      fetchCampaignsSummary(),
      fetchRoi(),
      fetchHeatmap(),
      fetchFunnel(),
      fetchCohort(),
    ]);
    setLoading(false);
  };

  const fetchLiveStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/live-stats`);
      setLiveStats(response.data);
    } catch (error) {
      console.error("Error fetching live stats:", error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const buildAnalyticsParams = () => {
    const params = new URLSearchParams({ days: effectiveDays });
    if (isCustom && customFrom) {
      params.append("date_from", customFrom);
      params.append("date_to", customTo || todayIso);
    }
    if (selectedCampaign !== "all")
      params.append("campaign_id", selectedCampaign);
    return params.toString();
  };

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/performance?${buildAnalyticsParams()}`,
      );
      setPerformanceData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching performance:", error);
      setPerformanceData([]);
    }
  };

  const fetchGeo = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/geo?${buildAnalyticsParams()}`,
      );
      setGeoData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching geo:", error);
      setGeoData([]);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/devices?${buildAnalyticsParams()}`,
      );
      setDeviceData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching devices:", error);
      setDeviceData([]);
    }
  };

  const fetchCampaignsSummary = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/campaigns-summary?${buildAnalyticsParams()}`,
      );
      setCampaignsSummary(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching campaigns summary:", error);
      setCampaignsSummary([]);
    }
  };

  const fetchRoi = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/roi?${buildAnalyticsParams()}`,
      );
      setRoiData(response.data || null);
    } catch (error) {
      console.error("Error fetching ROI:", error);
      setRoiData(null);
    }
  };

  const fetchHeatmap = async () => {
    try {
      const params = new URLSearchParams({ days: effectiveDays, metric: heatmapMetric });
      if (selectedCampaign !== "all") params.append("campaign_id", selectedCampaign);
      const response = await axios.get(`${API}/analytics/heatmap?${params}`);
      setHeatmapData(response.data || null);
    } catch (error) {
      console.error("Error fetching heatmap:", error);
      setHeatmapData(null);
    }
  };

  useEffect(() => {
    fetchHeatmap();
  }, [heatmapMetric]);

  const fetchFunnel = async () => {
    try {
      const response = await axios.get(
        `${API}/analytics/funnel?${buildAnalyticsParams()}`,
      );
      setFunnelData(response.data || null);
    } catch (error) {
      console.error("Error fetching funnel:", error);
      setFunnelData(null);
    }
  };

  const fetchCohort = async () => {
    try {
      const params = new URLSearchParams({ days: effectiveDays, offset_days: 7 });
      if (selectedCampaign !== "all") params.append("campaign_id", selectedCampaign);
      const response = await axios.get(`${API}/analytics/cohort?${params}`);
      setCohortData(response.data || null);
    } catch (error) {
      console.error("Error fetching cohort:", error);
      setCohortData(null);
    }
  };

  const exportReport = (format = 'json') => {
    const datestamp = new Date().toISOString().split("T")[0];
    if (format === 'csv' && performanceData.length > 0) {
      const cols = Object.keys(performanceData[0]);
      const header = cols.join(',');
      const body = performanceData.map((r) =>
        cols.map((c) => {
          const s = String(r[c] ?? '');
          return s.includes(',') || s.includes('"') || s.includes('\n')
            ? `"${s.replace(/"/g, '""')}"`
            : s;
        }).join(','),
      ).join('\n');
      const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${datestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(t("CSV exported"));
      return;
    }
    const reportData = {
      performanceData,
      geoData,
      deviceData,
      campaignsSummary,
      liveStats,
    };
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `analytics_${datestamp}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t("Report exported"));
  };

  if (loading) {
    return (
      <div className="px-8 pt-2 pb-8 space-y-6" data-testid="analytics-skeleton">
        <XpnsrPageHeader
          kicker={t("Analytics")}
          title={t("Performance overview")}
          subtitle={t("Live KPIs and breakdowns across campaigns, geos, devices.")}
        />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <XpnsrCardSkeleton key={i} rows={2} />
          ))}
        </div>
        <XpnsrCardSkeleton rows={1} withHeader={false} />
        <XpnsrTableSkeleton rows={6} columns={4} />
      </div>
    );
  }

  return (
    <div className="px-8 pt-2 pb-8 space-y-6" data-testid="analytics-view">
      <XpnsrPageHeader
        kicker={t("Analytics")}
        title={t("Performance overview")}
        subtitle={t("Live KPIs and breakdowns across campaigns, geos, devices.")}
        actions={
          <>
            <button
              type="button"
              className="xp-btn"
              onClick={fetchAll}
              data-testid="analytics-refresh-btn"
            >
              <RefreshCw style={{ width: 14, height: 14 }} />
              {t("Refresh")}
            </button>
            <button
              type="button"
              className="xp-btn"
              onClick={() => exportReport('json')}
              data-testid="analytics-export-btn"
            >
              <Download style={{ width: 14, height: 14 }} />
              {t("Export JSON")}
            </button>
            <button
              type="button"
              className="xp-btn"
              onClick={() => exportReport('csv')}
              data-testid="analytics-export-csv-btn"
            >
              <Download style={{ width: 14, height: 14 }} />
              {t("Export CSV")}
            </button>
          </>
        }
      />

      {liveStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("Campaigns")}
              </CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-campaigns">
                {liveStats.total_campaigns}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Active campaigns")}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("Clicks today")}
              </CardTitle>
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-clicks">
                {Number(liveStats.today_clicks || 0).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("For the current day")}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("Conversions")}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold"
                data-testid="stat-conversions"
              >
                {liveStats.today_conversions}
              </div>
              <p className="text-xs text-muted-foreground">{t("Confirmed")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">CR</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-cr">
                {num(liveStats.conversion_rate).toFixed(2)}%
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Conversion rate")}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">
                {t("Period:")}
              </Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger
                  className="w-36"
                  data-testid="analytics-days-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t("Today")}</SelectItem>
                  <SelectItem value="7">{t("7 days")}</SelectItem>
                  <SelectItem value="14">{t("14 days")}</SelectItem>
                  <SelectItem value="30">{t("30 days")}</SelectItem>
                  <SelectItem value="90">{t("90 days")}</SelectItem>
                  <SelectItem value="180">{t("180 days")}</SelectItem>
                  <SelectItem value="365">{t("365 days")}</SelectItem>
                  <SelectItem value="custom">{t("Custom range")}</SelectItem>
                </SelectContent>
              </Select>
              {days === 'custom' && (
                <div className="flex items-center gap-1">
                  <Input
                    type="date"
                    value={customFrom}
                    max={todayIso}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="w-36 h-9 text-xs"
                    aria-label={t('Period start')}
                  />
                  <span className="text-muted-foreground text-xs">–</span>
                  <Input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    max={todayIso}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="w-36 h-9 text-xs"
                    aria-label={t('Period end')}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">
                {t("Campaign:")}
              </Label>
              <Select
                value={selectedCampaign}
                onValueChange={setSelectedCampaign}
              >
                <SelectTrigger
                  className="w-48"
                  data-testid="analytics-campaign-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All campaigns")}</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="flex w-full flex-wrap h-auto justify-start">
          <TabsTrigger value="overview">{t("Overview")}</TabsTrigger>
          <TabsTrigger value="campaigns">{t("Campaigns")}</TabsTrigger>
          <TabsTrigger value="roi">ROI</TabsTrigger>
          <TabsTrigger value="geo">{t("Geo")}</TabsTrigger>
          <TabsTrigger value="devices">{t("Devices")}</TabsTrigger>
          <TabsTrigger value="heatmap">{t("Heatmap")}</TabsTrigger>
          <TabsTrigger value="funnel">{t("Funnel")}</TabsTrigger>
          <TabsTrigger value="cohort">{t("Cohort")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("Clicks and conversions")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {performanceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="clicks"
                        stroke="#00e5a0"
                        fill="#00e5a0"
                        fillOpacity={0.1}
                        name={t("Clicks")}
                      />
                      <Area
                        type="monotone"
                        dataKey="conversions"
                        stroke="#7af0c4"
                        fill="#7af0c4"
                        fillOpacity={0.1}
                        name={t("Conversions")}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t("No data for the selected period")}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("Hourly activity")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {liveStats?.hourly_stats ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={liveStats.hourly_stats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar
                        dataKey="clicks"
                        fill="#00e5a0"
                        name={t("Clicks")}
                        radius={[2, 2, 0, 0]}
                      />
                      <Bar
                        dataKey="conversions"
                        fill="#7af0c4"
                        name={t("Conversions")}
                        radius={[2, 2, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t("No data")}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("CR% by day")}</CardTitle>
            </CardHeader>
            <CardContent>
              {performanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(2)}%`} />
                    <Line
                      type="monotone"
                      dataKey="cr"
                      stroke="#00e5a0"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="CR%"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                  {t("No data")}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("Campaign stats")}</CardTitle>
            </CardHeader>
            <CardContent>
              {campaignsSummary.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Campaign")}</TableHead>
                      <TableHead className="text-right">
                        {t("Clicks")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("Conversions")}
                      </TableHead>
                      <TableHead className="text-right">CR%</TableHead>
                      <TableHead className="text-right">
                        {t("Revenue")}
                      </TableHead>
                      <TableHead>{t("Status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaignsSummary.map((item) => (
                      <TableRow key={item.campaign_id}>
                        <TableCell className="font-medium">
                          {item.campaign_name}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.clicks}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.conversions}
                        </TableCell>
                        <TableCell className="text-right">
                          {num(item.cr).toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right font-medium text-primary">
                          {formatMoney(item.revenue, item.currency || roiData?.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={item.is_active ? "default" : "secondary"}
                          >
                            {item.is_active ? t("Active") : t("Paused")}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  {t("No campaign data")}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roi" className="space-y-4">
          {roiData && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {t("All values shown in")}{" "}
                  <strong
                    className="text-foreground"
                    data-testid="roi-currency-badge"
                  >
                    {roiData.currency || "USD"}
                  </strong>
                </span>
                {roiData.fx_rates_available === false && (
                  <span
                    className="text-destructive"
                    data-testid="roi-fx-outage"
                  >
                    {t(
                      "FX rates unavailable — non-USD revenue is excluded from the totals.",
                    )}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      {t("Total revenue")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      className="text-2xl font-bold text-primary"
                      data-testid="roi-revenue"
                    >
                      {formatMoney(roiData.total_revenue, roiData.currency)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      {t("Total spend")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      className="text-2xl font-bold text-destructive"
                      data-testid="roi-cost"
                    >
                      {formatMoney(roiData.total_cost, roiData.currency)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      {t("Profit")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      className={`text-2xl font-bold ${num(roiData.total_profit) >= 0 ? "text-primary" : "text-destructive"}`}
                      data-testid="roi-profit"
                    >
                      {formatMoney(roiData.total_profit, roiData.currency)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">ROI</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      className={`text-2xl font-bold ${num(roiData.total_roi) >= 0 ? "text-primary" : "text-destructive"}`}
                      data-testid="roi-total"
                    >
                      {fmtRoi(roiData.total_roi, roiData.total_cost)}
                    </div>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {t("ROI by campaigns")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(roiData.campaigns || []).length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("Campaign")}</TableHead>
                          <TableHead className="text-right">
                            {t("Clicks")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("Conversions")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("Revenue")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("Spend")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("Profit")}
                          </TableHead>
                          <TableHead className="text-right">ROI</TableHead>
                          <TableHead className="text-right">EPC</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(roiData.campaigns || []).map((c) => (
                          <TableRow key={c.campaign_id}>
                            <TableCell className="font-medium">
                              {c.campaign_name}
                            </TableCell>
                            <TableCell className="text-right">
                              {c.clicks}
                            </TableCell>
                            <TableCell className="text-right">
                              {c.conversions}
                            </TableCell>
                            <TableCell className="text-right text-primary">
                              {formatMoney(c.revenue, roiData.currency)}
                            </TableCell>
                            <TableCell className="text-right text-destructive">
                              {formatMoney(c.cost, roiData.currency)}
                            </TableCell>
                            <TableCell
                              className={`text-right font-medium ${num(c.profit) >= 0 ? "text-primary" : "text-destructive"}`}
                            >
                              {formatMoney(c.profit, roiData.currency)}
                            </TableCell>
                            <TableCell
                              className={`text-right font-bold ${num(c.roi) >= 0 ? "text-primary" : "text-destructive"}`}
                            >
                              {fmtRoi(c.roi, c.cost)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatMoney(c.epc, roiData.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      {t("No ROI data")}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="geo" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("Clicks by country")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {geoData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={geoData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis
                        dataKey="country"
                        type="category"
                        width={80}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="clicks"
                        fill="#00e5a0"
                        name={t("Clicks")}
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t("No geo data")}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("Geo table")}</CardTitle>
              </CardHeader>
              <CardContent>
                {geoData.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("Country")}</TableHead>
                        <TableHead className="text-right">
                          {t("Clicks")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("Unique")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("Conversions")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {geoData.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">
                            {item.country}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.clicks}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.unique_clicks}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.conversions}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    {t("No geo data")}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("Device breakdown")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deviceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name} ${value}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {deviceData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    {t("No device data")}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("Devices table")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deviceData.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("Device type")}</TableHead>
                        <TableHead className="text-right">
                          {t("Clicks")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("Share")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deviceData.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">
                            {item.name}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.clicks}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.value}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    {t("No data")}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="heatmap" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Flame className="h-4 w-4" />
                    {t("Activity heatmap")}
                  </CardTitle>
                  <CardDescription>
                    {t("Day-of-week x hour-of-day breakdown")}
                    {heatmapData?.timezone && (
                      <span className="ml-1 text-xs">({heatmapData.timezone})</span>
                    )}
                  </CardDescription>
                </div>
                <Select value={heatmapMetric} onValueChange={setHeatmapMetric}>
                  <SelectTrigger className="w-40" data-testid="heatmap-metric-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clicks">{t("Clicks")}</SelectItem>
                    <SelectItem value="unique_clicks">{t("Unique clicks")}</SelectItem>
                    <SelectItem value="conversions">{t("Conversions")}</SelectItem>
                    <SelectItem value="revenue">{t("Revenue")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {heatmapData?.grid ? (
                <div className="overflow-x-auto" data-testid="heatmap-grid">
                  <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                    <span>{t("Total")}: <strong className="text-foreground">{num(heatmapData.total).toLocaleString()}</strong></span>
                    <span className="mx-1">|</span>
                    <span>{t("Peak")}: <strong className="text-foreground">{num(heatmapData.max).toLocaleString()}</strong></span>
                    <span className="mx-1">|</span>
                    <span>{t("Window")}: {heatmapData.window_days}d</span>
                  </div>
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr>
                        <th className="p-1 text-left font-medium text-muted-foreground w-12" />
                        {HOUR_LABELS.map((h) => (
                          <th key={h} className="p-0.5 text-center font-mono text-muted-foreground w-8">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(heatmapData.day_labels || []).map((dayLabel, rowIdx) => (
                        <tr key={dayLabel}>
                          <td className="p-1 font-medium text-muted-foreground whitespace-nowrap">{t(dayLabel)}</td>
                          {(heatmapData.grid[rowIdx] || []).map((val, colIdx) => (
                            <td
                              key={colIdx}
                              className={`p-0.5 text-center rounded-sm cursor-default transition-colors ${heatmapColor(val, heatmapData.max)}`}
                              title={`${dayLabel} ${HOUR_LABELS[colIdx]}:00 — ${val}`}
                            >
                              <span className="text-[10px] leading-none opacity-80">{val || ""}</span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                    <span>{t("Less")}</span>
                    {HEATMAP_CELL_COLORS.map((cls, i) => (
                      <div key={i} className={`w-4 h-4 rounded-sm ${cls}`} />
                    ))}
                    <span>{t("More")}</span>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  {t("No heatmap data")}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="funnel" className="space-y-4">
          {funnelData ? (
            <>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {t("All values shown in")}{" "}
                  <strong className="text-foreground">{funnelData.currency || "USD"}</strong>
                </span>
                {funnelData.fx_rates_available === false && (
                  <span className="text-destructive">
                    {t("FX rates unavailable — non-USD revenue is excluded from the totals.")}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{t("Total clicks")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="funnel-clicks">
                      {num(funnelData.clicks).toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{t("Unique clicks")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="funnel-unique">
                      {num(funnelData.unique_clicks).toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{t("Conversions")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary" data-testid="funnel-conversions">
                      {num(funnelData.conversions).toLocaleString()}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      CR: {num(funnelData.cr).toFixed(2)}% | Unique CR: {num(funnelData.unique_cr).toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{t("Revenue")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary" data-testid="funnel-revenue">
                      {formatMoney(funnelData.revenue, funnelData.currency)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <GitBranch className="h-4 w-4" />
                    {t("Conversion funnel")}
                  </CardTitle>
                  <CardDescription>
                    {t("Click-to-conversion pipeline with status breakdown")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {(funnelData.stages || []).length > 0 ? (
                    <div className="space-y-3" data-testid="funnel-stages">
                      {funnelData.stages.map((stage, idx) => {
                        const maxVal = Math.max(...funnelData.stages.map((s) => s.value));
                        const pct = maxVal > 0 ? (stage.value / maxVal) * 100 : 0;
                        return (
                          <div key={stage.name} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium">{t(stage.label)}</span>
                              <span className="text-muted-foreground">{num(stage.value).toLocaleString()}</span>
                            </div>
                            <div className="h-8 bg-muted rounded-md overflow-hidden">
                              <div
                                className="h-full rounded-md transition-all"
                                style={{
                                  width: `${Math.max(pct, 1)}%`,
                                  backgroundColor: COLORS[idx % COLORS.length],
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      {t("No funnel data")}
                    </div>
                  )}
                </CardContent>
              </Card>

              {Object.values(funnelData.breakdown || {}).some((v) => v > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t("Status breakdown")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart
                        data={Object.entries(funnelData.breakdown || {})
                          .filter(([, v]) => v > 0)
                          .map(([status, count]) => ({ status: status.charAt(0).toUpperCase() + status.slice(1), count }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#00e5a0" name={t("Conversions")} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {t("No funnel data")}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cohort" className="space-y-4">
          {cohortData && (cohortData.cohorts || []).length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("Cohort retention")}</CardTitle>
                <CardDescription>
                  {t(
                    "Each row is a day's first-time visitors. Columns track cumulative conversion rate by day-offset.",
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table
                    className="min-w-full text-xs border-separate border-spacing-0"
                    data-testid="cohort-table"
                  >
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 sticky left-0 bg-background">
                          {t("Cohort")}
                        </th>
                        <th className="text-right py-1 px-2">{t("Size")}</th>
                        {Array.from({ length: cohortData.offset_days || 0 }).map(
                          (_, i) => (
                            <th key={i} className="text-right py-1 px-2">
                              D{i}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {cohortData.cohorts.map((row) => (
                        <tr
                          key={row.cohort_date}
                          className="border-t border-muted"
                        >
                          <td className="py-1 px-2 sticky left-0 bg-background font-mono">
                            {row.cohort_date}
                          </td>
                          <td className="py-1 px-2 text-right">
                            {num(row.cohort_size).toLocaleString()}
                          </td>
                          {(row.rate_by_offset || []).map((r, i) => {
                            // Color intensity scales with rate so the
                            // operator can spot the retention curve at a
                            // glance — Mixpanel-style.
                            const intensity = Math.min(1, r * 4);
                            const bg =
                              row.cohort_size > 0
                                ? `rgba(0, 229, 160, ${intensity})`
                                : "transparent";
                            return (
                              <td
                                key={i}
                                className="py-1 px-2 text-right"
                                style={{ backgroundColor: bg }}
                                title={`${num(
                                  row.conversions_by_offset?.[i] || 0,
                                )} of ${num(row.cohort_size)}`}
                              >
                                {row.cohort_size > 0
                                  ? `${(r * 100).toFixed(1)}%`
                                  : "—"}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {cohortData.summary?.best_cohort && (
                  <p className="text-xs text-muted-foreground mt-3">
                    {t("Best cohort")}:{" "}
                    <strong className="text-foreground">
                      {cohortData.summary.best_cohort.cohort_date}
                    </strong>{" "}
                    ({(cohortData.summary.best_cohort.final_rate * 100).toFixed(1)}%)
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {t("No cohort data")}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
